import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import api from '../utils/api';
import { stripHtmlTags } from '../utils/htmlUtils';
import { Search, X, ChevronDown, ChevronUp, Filter, Star, MapPin } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { getTheme } from '../utils/themes';
import { useAdminEdit } from '../contexts/AdminEditContext';
import { useSubmitBusiness } from '../contexts/SubmitBusinessContext';
import EditableText from '../components/EditableText';
import EditableColor from '../components/EditableColor';
import EditableGradient from '../components/EditableGradient';
import EditableButton from '../components/EditableButton';
import AdminEditToggle from '../components/AdminEditToggle';
import PageLoader from '../components/PageLoader';
import { showSuccess, showError } from '../utils/toast';
import { isUserLoggedIn } from '../utils/auth';
import FavouriteButton from '../components/FavouriteButton';
import BusinessCard from '../components/BusinessCard';
import HeroSection from '../components/HeroSection';
import StatsCounter from '../components/StatsCounter';

const Home = () => {
  const { isEditMode, saveCustomization, getCustomization } = useAdminEdit();
  const { openModal } = useSubmitBusiness();
  const [categories, setCategories] = useState([]);
  const [featuredCategories, setFeaturedCategories] = useState([]);
  const [featuredSubcategories, setFeaturedSubcategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [featuredTags, setFeaturedTags] = useState([]);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [cities, setCities] = useState([]);
  const [stats, setStats] = useState({ businesses: 0, reviews: 0, users: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, featuredCategoriesWithBusinessesRes, featuredSubcategoriesRes, tagsRes, featuredTagsRes, citiesRes, statsRes, villagesRes] = await Promise.all([
          api.get('/categories'),
          api.get('/categories/featured/with-businesses'),
          api.get('/subcategories/featured'),
          api.get('/tags'),
          api.get('/tags/featured'),
          api.get('/cities'),
          api.get('/stats'),
          api.get('/villages'),
        ]);
        setCategories(categoriesRes.data);

        // The new endpoint returns categories with their businesses already grouped
        const categoriesWithBusinesses = featuredCategoriesWithBusinessesRes.data;

        // Extract featured categories for the category badges section
        const featuredCats = categoriesWithBusinesses.map(item => item.category);
        setFeaturedCategories(featuredCats);

        // Set the businesses by category directly
        setFeaturedBusinesses(categoriesWithBusinesses);

        setFeaturedSubcategories(featuredSubcategoriesRes.data);
        setTags(tagsRes.data);
        setFeaturedTags(featuredTagsRes.data);
        setCities(citiesRes.data);
        setStats(statsRes.data);
        setVillages(villagesRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!isUserLoggedIn()) return;
    api.get('/favourites').then((r) => {
      const list = r.data?.favourites || [];
      setFavouriteIds(new Set(list.map((b) => (b._id || b).toString())));
    }).catch(() => { });
  }, []);

  const handleAddFavourite = async (id) => {
    try {
      await api.post(`/favourites/${id}`);
      setFavouriteIds((prev) => new Set([...prev, id.toString()]));
      showSuccess('Added to favourites');
    } catch (e) {
      showError(e.response?.data?.message || 'Could not add to favourites');
    }
  };
  const handleRemoveFavourite = async (id) => {
    try {
      await api.delete(`/favourites/${id}`);
      setFavouriteIds((prev) => {
        const next = new Set(prev);
        next.delete(id.toString());
        return next;
      });
      showSuccess('Removed from favourites');
    } catch (e) {
      showError(e.response?.data?.message || 'Could not remove from favourites');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchTerm && searchTerm.trim()) params.set('q', searchTerm.trim());
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedCity && selectedCity !== 'all') {
      params.set('city', selectedCity);
    } else if (selectedCity === 'all') {
      params.set('city', 'all');
    }
    navigate(`/search?${params.toString()}`);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedCity('');
  };

  // The data now comes pre-grouped from the backend
  // featuredBusinesses is now an array of { category, businesses } objects
  const businessesByCategory = featuredBusinesses;

  const homeJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Digital Fatehabad',
      url: 'https://digitalfatehabad.com',
      description: 'Discover 10,000+ local businesses, shops, and services across Fatehabad district.',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: 'https://digitalfatehabad.com/search?q={search_term_string}'
        },
        'query-input': 'required name=search_term_string'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Digital Fatehabad',
      url: 'https://digitalfatehabad.com',
      logo: 'https://digitalfatehabad.com/digi_fatehabad_logo.png',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fatehabad',
        addressRegion: 'Haryana',
        postalCode: '125050',
        addressCountry: 'IN'
      }
    },
    ...(categories && categories.length > 0 ? [{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Top Business Categories in Fatehabad',
      itemListElement: categories.slice(0, 12).map((cat, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: cat.name,
        url: `https://digitalfatehabad.com/${cat.slug}`
      }))
    }] : [])
  ];

  return (
    <>
      <SEO
        title="10,000+ Local Businesses & Services in Fatehabad"
        description="Find verified shops, doctors, restaurants, schools, and services across Fatehabad, Tohana, Ratia, Bhuna, Uklana, Hisar & Sirsa. Real phone numbers, addresses, opening hours & reviews."
        canonical="/"
        jsonLd={homeJsonLd}
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <AdminEditToggle />

        {/* Modern Hero Section - New Design */}
        {!isEditMode && (
          <HeroSection
            onSearch={(term, location) => {
              setSearchTerm(term || '');
              setSelectedCity(location || '');
              const params = new URLSearchParams();
              if (term && term.trim()) params.set('q', term.trim());
              if (location && location !== 'all') {
                params.set('city', location);
              } else if (location === 'all') {
                params.set('city', 'all');
              }
              navigate(`/search?${params.toString()}`);
            }}
            categories={categories}
          />
        )}

        {/* Old Hero Section - For Edit Mode */}
        {isEditMode && (
          <div
            className="text-white relative group"
            style={{
              background: getCustomization('heroBackground', 'linear-gradient(to bottom right, #3b82f6, #1e40af, #1e3a8a)')
            }}
          >
            {isEditMode && (
              <div className="absolute top-4 right-4 z-10">
                <EditableGradient
                  value={getCustomization('heroBackground', 'linear-gradient(to bottom right, #3b82f6, #1e40af, #1e3a8a)')}
                  onSave={(value) => saveCustomization('heroBackground', value)}
                  className=""
                  label="Hero Background"
                />
              </div>
            )}
            <div className="container-custom py-10 md:py-14">
              <div className="text-center max-w-4xl mx-auto relative">
                <div className="text-2xl md:text-3xl font-semibold text-white mb-2">
                  <EditableText
                    value={getCustomization('heroTitle', 'Find Local Businesses in Fatehabad')}
                    onSave={(value) => saveCustomization('heroTitle', value)}
                    className="text-white"
                    tag="h1"
                  />
                </div>
                <div className="text-sm md:text-base text-white/90 mb-6">
                  <EditableText
                    value={getCustomization('heroSubtitle', 'Discover trusted businesses, services, and professionals near you')}
                    onSave={(value) => saveCustomization('heroSubtitle', value)}
                    className="text-white/90"
                    tag="p"
                    multiline
                  />
                </div>
                <div className="max-w-4xl mx-auto mb-6">
                  <form onSubmit={handleSearch} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 md:p-4">
                    {/* Search Input and Button Row */}
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search businesses, services, locations..."
                          className="w-full px-3 py-2 pl-9 pr-9 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50 placeholder-gray-500 dark:placeholder-gray-400"
                        />
                        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <EditableButton
                        type="submit"
                        textKey="searchButtonText"
                        bgColorKey="searchButtonBgColor"
                        textColorKey="searchButtonTextColor"
                        hoverBgColorKey="searchButtonHoverBgColor"
                        hoverTextColorKey="searchButtonHoverTextColor"
                        defaultText="Search"
                        defaultBgColor="#2563eb"
                        defaultTextColor="#ffffff"
                        defaultHoverBgColor="#1d4ed8"
                        defaultHoverTextColor="#ffffff"
                        className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm whitespace-nowrap rounded-md font-medium transition-colors"
                      >
                        <Search className="w-4 h-4" />
                        <span className="hidden sm:inline">{getCustomization('searchButtonText', 'Search')}</span>
                      </EditableButton>
                      <EditableButton
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        textKey="filtersButtonText"
                        bgColorKey="filtersButtonBgColor"
                        textColorKey="filtersButtonTextColor"
                        hoverBgColorKey="filtersButtonHoverBgColor"
                        hoverTextColorKey="filtersButtonHoverTextColor"
                        defaultText="Filters"
                        defaultBgColor="#ffffff"
                        defaultTextColor="#374151"
                        defaultHoverBgColor="#f9fafb"
                        defaultHoverTextColor="#374151"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md transition-colors flex items-center gap-1.5 text-sm whitespace-nowrap"
                      >
                        <Filter className="w-4 h-4" />
                        <span className="hidden sm:inline">{getCustomization('filtersButtonText', 'Filters')}</span>
                        {showFilters ? (
                          <ChevronUp className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )}
                      </EditableButton>
                    </div>

                    {/* Collapsible Filters */}
                    {showFilters && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* City Filter */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                              City
                            </label>
                            <select
                              value={selectedCity}
                              onChange={(e) => setSelectedCity(e.target.value)}
                              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50"
                            >
                              <option value="all">All Cities</option>
                              {cities.map(city => (
                                <option key={city._id} value={city._id}>{city.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Category Filter */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                              Category
                            </label>
                            <select
                              value={selectedCategory}
                              onChange={(e) => setSelectedCategory(e.target.value)}
                              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50"
                            >
                              <option value="">All Categories</option>
                              {categories.map(cat => (
                                <option key={cat._id} value={cat._id}>{cat.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {(selectedCategory || selectedCity) && (
                          <div className="mt-3">
                            <EditableButton
                              type="button"
                              onClick={handleClearFilters}
                              textKey="clearFiltersButtonText"
                              bgColorKey="clearFiltersButtonBgColor"
                              textColorKey="clearFiltersButtonTextColor"
                              hoverBgColorKey="clearFiltersButtonHoverBgColor"
                              hoverTextColorKey="clearFiltersButtonHoverTextColor"
                              defaultText="Clear Filters"
                              defaultBgColor="transparent"
                              defaultTextColor="#4b5563"
                              defaultHoverBgColor="transparent"
                              defaultHoverTextColor="#111827"
                              className="text-xs font-medium transition-colors flex items-center gap-1.5"
                            >
                              <X className="w-3.5 h-3.5" />
                              {getCustomization('clearFiltersButtonText', 'Clear Filters')}
                            </EditableButton>
                          </div>
                        )}
                      </div>
                    )}
                  </form>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <EditableButton
                    onClick={openModal}
                    textKey="listBusinessButtonText"
                    bgColorKey="listBusinessButtonBgColor"
                    textColorKey="listBusinessButtonTextColor"
                    hoverBgColorKey="listBusinessButtonHoverBgColor"
                    hoverTextColorKey="listBusinessButtonHoverTextColor"
                    defaultText="List Your Business"
                    defaultBgColor="#ffffff"
                    defaultTextColor="#1e40af"
                    defaultHoverBgColor="#f9fafb"
                    defaultHoverTextColor="#1e40af"
                    className="px-4 py-2 text-sm rounded-md font-medium transition-colors"
                  />
                  <EditableButton
                    to="/how-it-works"
                    textKey="howItWorksButtonText"
                    bgColorKey="howItWorksButtonBgColor"
                    textColorKey="howItWorksButtonTextColor"
                    hoverBgColorKey="howItWorksButtonHoverBgColor"
                    hoverTextColorKey="howItWorksButtonHoverTextColor"
                    defaultText="How It Works"
                    defaultBgColor="rgba(255, 255, 255, 0.1)"
                    defaultTextColor="#ffffff"
                    defaultHoverBgColor="rgba(255, 255, 255, 0.2)"
                    defaultHoverTextColor="#ffffff"
                    className="px-4 py-2 text-sm rounded-md font-medium border border-white/20 transition-colors backdrop-blur-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Counter */}
        <StatsCounter
          businesses={stats.businesses}
          reviews={stats.reviews}
          users={stats.users}
        />

        <div className="container-custom py-16">

          {loading ? (
            <PageLoader message={getCustomization('loadingText', 'Loading businesses...')} />
          ) : (
            <>
              {/* Featured Businesses by Category */}
              {businessesByCategory.length > 0 && (
                <section className="mb-20">
                  <div className="mb-6">
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-50 mb-1">
                      <EditableText
                        value={getCustomization('featuredBusinessesTitle', 'Featured Businesses')}
                        onSave={(value) => saveCustomization('featuredBusinessesTitle', value)}
                        className="text-gray-900 dark:text-gray-50"
                        tag="span"
                      />
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      <EditableText
                        value={getCustomization('featuredBusinessesSubtitle', 'Recommended businesses by category')}
                        onSave={(value) => saveCustomization('featuredBusinessesSubtitle', value)}
                        className="text-gray-600 dark:text-gray-400"
                        tag="span"
                      />
                    </p>
                  </div>
                  <div className="space-y-12">
                    {businessesByCategory.map((group) => (
                      <div key={group.category._id} className="mb-8">
                        {/* Category Header */}
                        {(() => {
                          const theme = getTheme(group.category.theme);
                          // Use primary color for icon and category name (theme color)
                          return (
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                {group.category.icon && (() => {
                                  const IconComponent = LucideIcons[group.category.icon] || LucideIcons.Folder;
                                  return <IconComponent className="w-6 h-6 flex-shrink-0" style={{ color: theme.colors.primary }} />;
                                })()}
                                <div>
                                  <h3 className="text-xl font-semibold mb-1" style={{ color: theme.colors.primary }}>
                                    {group.category.name}
                                  </h3>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {group.businesses.length} {group.businesses.length === 1 ? 'business' : 'businesses'}
                                  </p>
                                </div>
                              </div>
                              <Link
                                to={`/${group.category.slug}`}
                                className="text-sm font-medium flex items-center gap-1 transition-colors"
                                style={{
                                  color: theme.colors.primary
                                }}
                                onMouseEnter={(e) => e.target.style.color = theme.colors.hover}
                                onMouseLeave={(e) => e.target.style.color = theme.colors.primary}
                              >
                                <EditableText
                                  value={getCustomization('viewAllText', 'View All')}
                                  onSave={(value) => saveCustomization('viewAllText', value)}
                                  className=""
                                  tag="span"
                                />
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </Link>
                            </div>
                          );
                        })()}

                        {/* Businesses Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 md:gap-4 divide-y md:divide-y-0 divide-gray-100 dark:divide-gray-800">
                          {group.businesses.map((business) => (
                            <BusinessCard
                              key={business._id}
                              business={business}
                              favouriteIds={favouriteIds}
                              onAddFavourite={handleAddFavourite}
                              onRemoveFavourite={handleRemoveFavourite}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Categories Section */}
              {categories.length > 0 && (
                <section className="mb-20 relative">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-50 mb-1">
                        <EditableText
                          value={getCustomization('categoriesTitle', 'Browse Categories')}
                          onSave={(value) => saveCustomization('categoriesTitle', value)}
                          className="text-gray-900 dark:text-gray-50"
                          tag="span"
                        />
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        <EditableText
                          value={getCustomization('categoriesSubtitle', 'Explore all business categories')}
                          onSave={(value) => saveCustomization('categoriesSubtitle', value)}
                          className="text-gray-600 dark:text-gray-400"
                          tag="span"
                        />
                      </p>
                    </div>
                    <Link
                      to="/categories"
                      className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                    >
                      <EditableText
                        value={getCustomization('viewAllCategoriesText', 'View All →')}
                        onSave={(value) => saveCustomization('viewAllCategoriesText', value)}
                        className="text-primary-600 dark:text-primary-400"
                        tag="span"
                      />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {categories.map((category) => {
                      const isFeatured = featuredCategories.some(fc => fc._id === category._id);
                      const IconComponent = category.icon ? (LucideIcons[category.icon] || LucideIcons.Folder) : LucideIcons.Folder;
                      const theme = getTheme(category.theme);

                      return (
                        <Link
                          key={category._id}
                          to={`/${category.slug}`}
                          className={`flex flex-col items-center justify-center p-6 rounded-xl border transition-all duration-300 group hover:-translate-y-1 hover:shadow-lg ${isFeatured
                            ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
                            : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-800'
                            }`}
                        >
                          <div
                            className={`p-3 rounded-full mb-3 ${isFeatured ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' : 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/50'}`}
                          >
                            <IconComponent className="w-6 h-6" strokeWidth={2} />
                          </div>
                          <span className={`text-sm font-semibold text-center ${isFeatured ? 'text-amber-900 dark:text-amber-100' : 'text-gray-900 dark:text-gray-100 group-hover:text-primary-700 dark:group-hover:text-primary-300'}`}>
                            {category.name}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Explore Villages Section */}
              {villages.length > 0 && (
                <section className="mb-20">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-1 flex items-center gap-2">
                        <MapPin className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                        Explore Villages of Fatehabad (270+ गांव)
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        फतेहाबाद जिले के सभी 7 ब्लॉकों के अटल सेवा केंद्र, स्कूल, स्वास्थ्य केंद्र व स्थानीय दुकानें
                      </p>
                    </div>
                    <Link
                      to="/villages"
                      className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 flex items-center gap-1 shrink-0"
                    >
                      View All 274 Villages →
                    </Link>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {villages.slice(0, 18).map((v) => (
                      <Link
                        key={v._id}
                        to={`/village/${v.slug}`}
                        className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-700 hover:shadow-md transition-all text-center flex flex-col justify-center group"
                      >
                        <span className="font-semibold text-sm text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors truncate">
                          {v.name}
                        </span>
                        <span className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                          {v.block} Block
                        </span>
                      </Link>
                    ))}
                  </div>

                  <div className="mt-6 text-center">
                    <Link
                      to="/villages"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 font-semibold text-sm transition-all shadow-sm"
                    >
                      Browse All 274 Villages Directory ({villages.length}) →
                    </Link>
                  </div>
                </section>
              )}

              {/* Tags Section */}
              {featuredTags.length > 0 && (
                <section className="mb-20">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-50 mb-1">
                        <EditableText
                          value={getCustomization('tagsTitle', 'Popular Tags')}
                          onSave={(value) => saveCustomization('tagsTitle', value)}
                          className="text-gray-900 dark:text-gray-50"
                          tag="span"
                        />
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        <EditableText
                          value={getCustomization('tagsSubtitle', 'Discover businesses by tags')}
                          onSave={(value) => saveCustomization('tagsSubtitle', value)}
                          className="text-gray-600 dark:text-gray-400"
                          tag="span"
                        />
                      </p>
                    </div>
                    {tags.length > featuredTags.length && (
                      <Link
                        to="/tags"
                        className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        <EditableText
                          value={getCustomization('viewAllTagsText', 'View All →')}
                          onSave={(value) => saveCustomization('viewAllTagsText', value)}
                          className="text-primary-600 dark:text-primary-400"
                          tag="span"
                        />
                      </Link>
                    )}
                  </div>
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm">
                    <div className="flex flex-wrap gap-3">
                      {featuredTags.map((tag) => (
                        <Link
                          key={tag._id}
                          to={`/tag/${tag.slug}`}
                          className="px-4 py-2 rounded-full text-sm font-medium transition-all bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-primary-600 hover:text-white hover:border-primary-600 dark:hover:bg-primary-500 dark:hover:border-primary-500"
                        >
                          {tag.name}
                        </Link>
                      ))}
                    </div>
                    {tags.length > featuredTags.length && (
                      <div className="mt-6 text-center">
                        <Link
                          to="/tags"
                          className="btn-primary inline-block"
                        >
                          <EditableText
                            value={getCustomization('viewAllTagsButtonText', 'View All Tags')}
                            onSave={(value) => saveCustomization('viewAllTagsButtonText', value)}
                            className=""
                            tag="span"
                          />
                          {' '}({tags.length})
                        </Link>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Local SEO Authority & City Crawl Hub */}
              <section className="bg-white dark:bg-gray-900 rounded-3xl p-6 md:p-10 shadow-sm border border-gray-100 dark:border-gray-800 mb-16">
                <div className="max-w-4xl mx-auto space-y-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white mb-2">
                      Digital Fatehabad - Haryana's Leading Local Business Directory
                    </h2>
                    <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                      Welcome to <strong>Digital Fatehabad</strong>, your one-stop digital portal connecting local residents with over <strong>10,000+ verified businesses</strong>, shops, doctors, emergency services, and professionals across Fatehabad district and neighboring towns in Haryana. Whether you need an experienced doctor, a trusted automobile mechanic in Tohana, agricultural supplies in Ratia, or grocery shopping in Bhuna, find accurate phone numbers, shop addresses, timings, and authentic reviews.
                    </p>
                  </div>

                  {/* Explore by Major Cities */}
                  <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider mb-3">
                      Explore Businesses by Town & Region
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { name: 'Fatehabad', path: '/search?city=fatehabad' },
                        { name: 'Tohana', path: '/search?city=tohana' },
                        { name: 'Ratia', path: '/search?city=ratia' },
                        { name: 'Bhuna', path: '/search?city=bhuna' },
                        { name: 'Bhattu Kalan', path: '/search?city=bhattu' },
                        { name: 'Jakhal Mandi', path: '/search?city=jakhal' },
                        { name: 'Uklana', path: '/search?city=uklana' },
                        { name: 'Hisar', path: '/search?city=hisar' },
                        { name: 'Sirsa', path: '/search?city=sirsa' },
                        { name: 'Explore 240+ Villages', path: '/villages' },
                        { name: 'Haryana Roadways Bus Timetable', path: '/bus-time-table' }
                      ].map((loc, idx) => (
                        <Link
                          key={idx}
                          to={loc.path}
                          className="text-sm font-semibold px-3.5 py-2 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-950 dark:hover:text-primary-300 border border-gray-200 dark:border-gray-700 transition-colors shadow-2xs"
                        >
                          {loc.name}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Popular Categories Fast Links */}
                  <div className="pt-2">
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider mb-3">
                      Popular Categories in Fatehabad
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {categories.slice(0, 16).map((cat) => (
                        <Link
                          key={cat._id}
                          to={`/${cat.slug}`}
                          className="text-xs font-medium px-3 py-1 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        >
                          {cat.name}
                        </Link>
                      ))}
                      <Link
                        to="/categories"
                        className="text-xs font-bold px-3 py-1 rounded-lg bg-primary-50 dark:bg-primary-950 text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        All Categories →
                      </Link>
                    </div>
                  </div>
                </div>
              </section>

              {/* Call to Action */}
              <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-900 via-primary-800 to-primary-600 text-white mb-16 shadow-xl">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-64 h-64 bg-primary-500/20 rounded-full blur-3xl pointer-events-none"></div>

                <div className="relative z-10 p-8 md:p-16 text-center">
                  <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">
                    <EditableText
                      value={getCustomization('ctaTitle', 'Ready to Grow Your Business?')}
                      onSave={(value) => saveCustomization('ctaTitle', value)}
                      className="text-white drop-shadow-sm"
                      tag="span"
                    />
                  </h2>
                  <p className="text-primary-100 mb-10 max-w-2xl mx-auto text-lg md:text-xl leading-relaxed">
                    <EditableText
                      value={getCustomization('ctaDescription', 'Join thousands of businesses already listed on Digital Fatehabad. Get discovered by customers in your area.')}
                      onSave={(value) => saveCustomization('ctaDescription', value)}
                      className="text-primary-100"
                      tag="span"
                      multiline={true}
                    />
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <EditableButton
                      onClick={openModal}
                      textKey="ctaButton1Text"
                      bgColorKey="ctaButton1BgColor"
                      textColorKey="ctaButton1TextColor"
                      hoverBgColorKey="ctaButton1HoverBgColor"
                      hoverTextColorKey="ctaButton1HoverTextColor"
                      defaultText="List Your Business Free"
                      defaultBgColor="#ffffff"
                      defaultTextColor="#1e40af"
                      defaultHoverBgColor="#f8fafc"
                      defaultHoverTextColor="#1e3a8a"
                      className="px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5"
                    />
                    <EditableButton
                      to="/about"
                      textKey="ctaButton2Text"
                      bgColorKey="ctaButton2BgColor"
                      textColorKey="ctaButton2TextColor"
                      hoverBgColorKey="ctaButton2HoverBgColor"
                      hoverTextColorKey="ctaButton2HoverTextColor"
                      defaultText="Learn More"
                      defaultBgColor="rgba(255, 255, 255, 0.1)"
                      defaultTextColor="#ffffff"
                      defaultHoverBgColor="rgba(255, 255, 255, 0.2)"
                      defaultHoverTextColor="#ffffff"
                      className="px-8 py-4 rounded-xl font-semibold text-lg border border-white/20 hover:bg-white/10 transition-all backdrop-blur-sm"
                    />
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </div >
    </>
  );
};

export default Home;

